import type { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import type { BreadcrumbItem } from 'src/UI/app-container/BreadcrumbTrail';
import { householdName } from './householdName';

type Owner = {
  id: string;
  familyId?: string | null;
  firstName?: string;
  lastName?: string;
};

/**
 * The trail above a financial item: All clients, then where the adviser was working, then that
 * view's Financials.
 *
 * A record always hangs off one client, but the same item reads differently depending on where it
 * was opened from. Opened from the family it is the household's item, so the crumb names the
 * household and leads back to it; opened from Alice Smith's own screens it is Alice's, so it names
 * Alice and leads back to her — landing on the household there would be a crumb to somewhere the
 * adviser has not been.
 *
 * {@link RETURN_TO_PARAM} is what carries that origin, and only a family view sets it. The id
 * comes from the client's own `familyId` rather than from the parameter, which arrives through the
 * query string and is not ours to trust.
 */
export function financialItemCrumbs(
  owner: Owner,
  members: FamilyMember[],
  returnTo?: string | null,
): BreadcrumbItem[] {
  const clientName = [owner.firstName, owner.lastName].filter(Boolean).join(' ');
  const underHousehold = openedFromFamily(returnTo) && Boolean(owner.familyId);

  return [
    { label: 'All clients', href: '/clients/all' },
    underHousehold
      ? {
          label: householdName(members, clientName),
          href: `/family/${owner.familyId}/profile`,
          highlight: true,
        }
      : {
          label: clientName,
          href: `/client/${owner.id}/non-financial-data`,
          highlight: true,
        },
    {
      label: 'Financials',
      href: underHousehold
        ? `/family/${owner.familyId}/financial`
        : `/client/${owner.id}/financial-data`,
    },
  ];
}

/** Only a family view carries a return path, and it always points back into `/family/`. */
function openedFromFamily(returnTo?: string | null): boolean {
  return returnTo?.startsWith('/family/') ?? false;
}
